import { EditorContent, EditorContext, useEditor } from '@tiptap/react'
import { useEffect, useRef } from 'react'

// --- Tiptap Core Extensions ---
import { Highlight } from '@tiptap/extension-highlight'
import { Image } from '@tiptap/extension-image'
import { Subscript } from '@tiptap/extension-subscript'
import { Superscript } from '@tiptap/extension-superscript'
import { TextAlign } from '@tiptap/extension-text-align'
import { Typography } from '@tiptap/extension-typography'
import { Placeholder, Selection } from '@tiptap/extensions'
import { StarterKit } from '@tiptap/starter-kit'

// --- UI Primitives ---
import { Toolbar } from '@components/tiptap/tiptap-ui-primitive/toolbar'

// --- Tiptap Node ---
import '@components/tiptap/tiptap-node/blockquote-node/blockquote-node.scss'
import '@components/tiptap/tiptap-node/code-block-node/code-block-node.scss'
import '@components/tiptap/tiptap-node/heading-node/heading-node.scss'
import { HorizontalRule } from '@components/tiptap/tiptap-node/horizontal-rule-node/horizontal-rule-node-extension'
import '@components/tiptap/tiptap-node/horizontal-rule-node/horizontal-rule-node.scss'
import '@components/tiptap/tiptap-node/image-node/image-node.scss'
import { ImageUploadNode } from '@components/tiptap/tiptap-node/image-upload-node/image-upload-node-extension'
import '@components/tiptap/tiptap-node/list-node/list-node.scss'
import '@components/tiptap/tiptap-node/paragraph-node/paragraph-node.scss'
import { TextStyle } from '@tiptap/extension-text-style'

// --- Tiptap UI ---
import { NodeAlignment } from '@components/tiptap/tiptap-extension/node-alignment-extension'
import { NodeBackground } from '@components/tiptap/tiptap-extension/node-background-extension'

// --- Lib ---
import { handleImageUpload, MAX_FILE_SIZE } from '@utils/tiptap-utils'

// --- Styles ---
import '@components/tiptap/tiptap-templates/simple/simple-editor.scss'

import { TableHandleExtension } from '@components/tiptap/tiptap-node/table-node/extensions/table-handle'
import { TableKit } from '@components/tiptap/tiptap-node/table-node/extensions/table-node-extension'

import { TableCellHandleMenu } from '@components/tiptap/tiptap-node/table-node/ui/table-cell-handle-menu'
import { TableExtendRowColumnButtons } from '@components/tiptap/tiptap-node/table-node/ui/table-extend-row-column-button'
import { TableHandle } from '@components/tiptap/tiptap-node/table-node/ui/table-handle/table-handle'
import { TableSelectionOverlay } from '@components/tiptap/tiptap-node/table-node/ui/table-selection-overlay'

// Import required styles
import { VariableHighlight } from '@components/tiptap/tiptap-extension/variable-extention'
import '@components/tiptap/tiptap-node/table-node/styles/prosemirror-table.scss'
import '@components/tiptap/tiptap-node/table-node/styles/table-node.scss'
import { Flex, theme } from 'antd'
import { MainToolbarContent } from './toolbar'

type Props = {
  value?: string
  onChange?: (value: string) => void
  mode?: 'simple' | 'full'
  disabled?: boolean
}

const RichTextEditor = ({
  value,
  onChange,
  mode = 'full',
  disabled,
}: Props) => {
  const toolbarRef = useRef<HTMLDivElement>(null)
  const { token } = theme.useToken()

  // Configuration des extensions en fonction du mode
  const getExtensions = () => {
    const extensions: any[] = [
      StarterKit.configure({
        horizontalRule: false,
        heading:
          mode === 'simple' ? { levels: [] } : { levels: [1, 2, 3, 4, 5, 6] },
        blockquote: mode === 'simple' ? false : {},
        codeBlock: mode === 'simple' ? false : {},
        code: mode === 'simple' ? false : {},
        bulletList: mode === 'simple' ? false : {},
        orderedList: mode === 'simple' ? false : {},
      }),
      Placeholder.configure({
        placeholder: 'Start to type here',
      }),
      Typography,
      Selection,
      VariableHighlight,
    ]

    // Extensions disponibles en mode full uniquement
    if (mode === 'full') {
      extensions.push(
        TableKit.configure({
          table: {
            resizable: true,
          },
        }),
        TableHandleExtension,
        HorizontalRule,
        TextStyle,
        NodeAlignment,
        NodeBackground,
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        Highlight.configure({ multicolor: true }),
        Image,
        Superscript,
        Subscript,
        ImageUploadNode.configure({
          accept: 'image/*',
          maxSize: MAX_FILE_SIZE,
          limit: 3,
          upload: handleImageUpload,
          onError: (error) => console.error('Upload failed:', error),
        })
      )
    }

    return extensions
  }

  const editor = useEditor(
    {
      immediatelyRender: false,
      content: value || '',
      editorProps: {
        attributes: {
          autocomplete: 'off',
          autocorrect: 'off',
          autocapitalize: 'off',
          'aria-label': 'Main content area, start typing to enter text.',
          class: `simple-editor ${mode}-mode`,
        },
      },
      editable: !disabled,
      extensions: getExtensions(),
      onUpdate: ({ editor }) => {
        const html = editor.getHTML()
        onChange?.(html)
      },
    },
    [mode]
  )

  // Synchroniser les changements externes du value
  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      const { from, to } = editor.state.selection
      editor.commands.setContent(value)
      // Restaurer la position du curseur si possible
      editor.commands.setTextSelection({ from, to })
    }
  }, [editor, value])

  return (
    <EditorContext.Provider value={{ editor }}>
      <Flex
        vertical
        style={{
          borderWidth: 1,
          borderColor: token.colorBorder,
          borderStyle: 'solid',
          borderRadius: 10,
          overflow: 'hidden',
          opacity: disabled ? 0.7 : undefined,
          cursor: disabled ? 'not-allowed' : undefined,
        }}
      >
        {!disabled && (
          <Toolbar ref={toolbarRef}>
            <MainToolbarContent mode={mode} />
          </Toolbar>
        )}
        <Flex
          vertical
          style={{
            background: token.colorBgLayout,
            paddingTop: 20,
            paddingBottom: 30,
          }}
        >
          <EditorContent
            editor={editor}
            role="presentation"
            className={`simple-editor-content`}
          />
        </Flex>

        {/* Composants de table seulement en mode full */}
        {mode === 'full' && (
          <>
            <TableHandle />
            <TableSelectionOverlay
              showResizeHandles={true}
              cellMenu={(props) => (
                <TableCellHandleMenu
                  editor={props.editor}
                  onMouseDown={(e) => props.onResizeStart?.('br')(e)}
                />
              )}
            />
            <TableExtendRowColumnButtons />
          </>
        )}
      </Flex>
    </EditorContext.Provider>
  )
}

export default RichTextEditor
